/**
 * Preview Mode Indicator
 * 
 * Visual indicator shown when the SDK is in draft/preview mode
 * Helps users distinguish between preview and production environments
 */

import { useEffect, useState } from 'react';
import { isDraftMode, isFunnelTrackingEnabled, getSDKParams } from '../../core/utils/previewMode';

export interface PreviewModeIndicatorProps {
  /**
   * Position of the indicator
   * @default 'bottom-right'
   */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  
  /**
   * Custom className for styling
   */
  className?: string;
  
  /**
   * Show detailed info on hover
   * @default true
   */
  showDetails?: boolean;
}

/**
 * Preview Mode Indicator Component
 * 
 * Automatically shows when:
 * - Draft mode is enabled (draft=true)
 * - Funnel tracking is disabled (funnelTracking=false)
 * - Custom API environment is set (tagadaClientEnv)
 * - Custom API base URL is set (tagadaClientBaseUrl)
 * 
 * @example
 * ```tsx
 * import { PreviewModeIndicator } from '@tagadapay/plugin-sdk';
 * 
 * function App() {
 *   return (
 *     <>
 *       <PreviewModeIndicator />
 *       {/* Your app content *\/}
 *     </>
 *   );
 * }
 * ```
 */
export function PreviewModeIndicator({
  position = 'bottom-right',
  className,
  showDetails = true,
}: PreviewModeIndicatorProps = {}) {
  const [isVisible, setIsVisible] = useState(false);
  const [params, setParams] = useState<ReturnType<typeof getSDKParams>>({});
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Check if we're in preview mode
    const sdkParams = getSDKParams();
    const draftMode = isDraftMode();
    const trackingDisabled = !isFunnelTrackingEnabled();
    const hasCustomEnv = !!(sdkParams.tagadaClientEnv || sdkParams.tagadaClientBaseUrl);

    setParams(sdkParams);
    setIsVisible(draftMode || trackingDisabled || hasCustomEnv);
  }, []);

  if (!isVisible) return null;

  const positionStyles = {
    'top-left': { top: '16px', left: '16px' },
    'top-right': { top: '16px', right: '16px' },
    'bottom-left': { bottom: '16px', left: '16px' },
    'bottom-right': { bottom: '16px', right: '16px' },
  };

  const isDraft = isDraftMode();
  const trackingDisabled = !isFunnelTrackingEnabled();

  return (
    <div
      className={className}
      style={{
        position: 'fixed',
        zIndex: 999999,
        ...positionStyles[position],
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
      onMouseEnter={() => showDetails && setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Main Badge */}
      <div
        style={{
          background: isDraft ? '#ff9500' : '#007aff',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: '600',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          cursor: showDetails ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        <span style={{ fontSize: '16px' }}>🔍</span>
        <span>{isDraft ? 'Preview Mode' : 'Dev Mode'}</span>
      </div>

      {/* Expanded Details */}
      {showDetails && isExpanded && (
        <div
          style={{
            position: 'absolute',
            bottom: position.includes('bottom') ? 'calc(100% + 8px)' : undefined,
            top: position.includes('top') ? 'calc(100% + 8px)' : undefined,
            right: position.includes('right') ? 0 : undefined,
            left: position.includes('left') ? 0 : undefined,
            background: 'white',
            border: '1px solid #e5e5e5',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            padding: '12px',
            minWidth: '250px',
            fontSize: '12px',
            lineHeight: '1.5',
          }}
        >
          <div style={{ marginBottom: '8px', fontWeight: '600', color: '#1d1d1f' }}>
            Current Environment
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {isDraft && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#86868b' }}>
                <span>Draft Mode:</span>
                <span style={{ color: '#ff9500', fontWeight: '600' }}>ON</span>
              </div>
            )}
            
            {trackingDisabled && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#86868b' }}>
                <span>Tracking:</span>
                <span style={{ color: '#ff3b30', fontWeight: '600' }}>DISABLED</span>
              </div>
            )}
            
            {params.funnelEnv && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#86868b' }}>
                <span>Funnel Env:</span>
                <span style={{ color: '#1d1d1f', fontWeight: '600', fontFamily: 'monospace', fontSize: '11px' }}>
                  {params.funnelEnv}
                </span>
              </div>
            )}
            
            {params.tagadaClientEnv && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#86868b' }}>
                <span>API Env:</span>
                <span style={{ color: '#1d1d1f', fontWeight: '600', fontFamily: 'monospace', fontSize: '11px' }}>
                  {params.tagadaClientEnv}
                </span>
              </div>
            )}
            
            {params.tagadaClientBaseUrl && (
              <div style={{ color: '#86868b' }}>
                <div style={{ marginBottom: '4px' }}>API URL:</div>
                <div
                  style={{
                    color: '#1d1d1f',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    fontSize: '10px',
                    wordBreak: 'break-all',
                    background: '#f5f5f7',
                    padding: '4px 6px',
                    borderRadius: '4px',
                  }}
                >
                  {params.tagadaClientBaseUrl}
                </div>
              </div>
            )}
            
            {params.funnelId && (
              <div style={{ color: '#86868b', marginTop: '4px', paddingTop: '8px', borderTop: '1px solid #e5e5e5' }}>
                <div style={{ marginBottom: '4px' }}>Funnel ID:</div>
                <div
                  style={{
                    color: '#1d1d1f',
                    fontFamily: 'monospace',
                    fontSize: '10px',
                    wordBreak: 'break-all',
                    background: '#f5f5f7',
                    padding: '4px 6px',
                    borderRadius: '4px',
                  }}
                >
                  {params.funnelId}
                </div>
              </div>
            )}
          </div>
          
          <div
            style={{
              marginTop: '12px',
              paddingTop: '8px',
              borderTop: '1px solid #e5e5e5',
              fontSize: '11px',
              color: '#86868b',
              textAlign: 'center',
            }}
          >
            Add <code style={{ background: '#f5f5f7', padding: '2px 4px', borderRadius: '3px' }}>?forceReset=true</code> to reset
          </div>
        </div>
      )}
    </div>
  );
}


